/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import GridContainer from '../../components/Grid/GridContainer';
import Filter from '../../interfaces/forms/Filter';
import Order from '../../interfaces/forms/Order';
import { useDispatch, useSelector } from 'react-redux';
import { FilterReducerFieldName, FilterReducers } from '../../modules/filter/FilterReducers';
import { makeStyles } from '@material-ui/core/styles';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import GridItem from '../../components/Grid/GridItem';
import PaperedTable from '../../components/Table/PaperedTable';
import { TableBody, TableFooter, TableHead, TableRow } from '@material-ui/core';
import TableCell from '@material-ui/core/TableCell';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import FilterList from '../../components/Filter/FilterList';
import I18n from '../../components/Translate/I18n';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import Pager from '../../components/Adescom/PagerRange';
import { FilterListReducerFieldName } from '../../modules/filter/FilterListReducers';
import { history } from '../../core/history';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FilterManager } from '../../modules/filter/FilterManager';
import FabAddButton from '../../components/Buttons/FabAddButton';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import { MvnoOperationSetupCollectionInterface } from '../../interfaces/platform/MvnoOperatorSetup';
import { MnoOperatorSetupAction, MnoOperatorSetupReducersType } from '../../modules/mvnoOperatorSetup/MnoOperatorSetupReducers';
import { ServiceProfileItem } from './ServiceProfileItem';
import { ServiceProfileAddNew } from './ServiceProfileAddNew';

const useStyle = makeStyles(adescomStyle);

type ServiceProfileListType = {
    showMainLoader(show: boolean): void;
};

const setDefaultFilters = (): Array<Filter> => [{ name: 'systemRestrictedFlag', value: false }];
const setDefaultOrders = (): Array<Order> => [{ name: 'templateName', value: 'asc' }];

export const ServiceProfileList: FunctionComponent<ServiceProfileListType> = ({ showMainLoader }): ReactElement => {
    const dispatch = useDispatch();
    const classes = useStyle();
    const { headerFilterElementHasValue, headerFilterElementName } = useSelector((state: { filterReducer: FilterReducers }) => state.filterReducer);
    const [filters, setFilters] = useState<Array<Filter>>(setDefaultFilters());
    const [orders, setOrders] = useState<Array<Order>>(setDefaultOrders());
    const [activeRow, setActiveRow] = useState(-1);
    const [reload, setReload] = useState(false);
    const [newTemplate, setNewTemplate] = useState(false);
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const { mvnoOperatorSetupList } = useSelector(
        (state: { mnoOperatorSetupReducers: MnoOperatorSetupReducersType }) => state.mnoOperatorSetupReducers
    );

    const clearFilter = (): void => {
        setFilters(setDefaultFilters());
        setOrders(setDefaultOrders());
        setShowClearFilterButton(false);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    useEffect(() => {
        const filterManage = new FilterManager();
        if (headerFilterElementName) {
            if (headerFilterElementHasValue) {
                filterManage.manageElement(headerFilterElementName, headerFilterElementHasValue);
            } else {
                filterManage.manageElement(headerFilterElementName, false);
            }
            setShowClearFilterButton(filterManage.hasManageElement());
        }
    }, [headerFilterElementName, headerFilterElementHasValue, dispatch]);

    useEffect(() => {
        setReload(true);
    }, [orders]);

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);
    const firstLoad = (): void => {};

    const viewCallback = (iri: { uri: string }, event: any): void => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        history.respectModifierPush(event, FormGeneralHelper.getRouteUrl('/service-profile-edit' + iri.uri));
    };

    const deleteCallback = (iri: { uri: string }, event: any): void => {};

    const paginationCallbackFunction = (uri: string): void => {
        showMainLoader(true);
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.paginationCallWithCallback(
            uri,
            'mno_operator_setup_types_list_load',
            (data: MvnoOperationSetupCollectionInterface): void => {
                dispatch({
                    type: MnoOperatorSetupAction.MNO_OPERATOR_SET_GET_LIST,
                    mvnoOperatorSetupList: data,
                } as MnoOperatorSetupReducersType);
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    useEffect(() => {
        if (reload) {
            setReload(false);
            showMainLoader(true);
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mvno_operator_setups')),
                'mno_operator_setup_types_list_load',
                filters,
                orders,
                (data: MvnoOperationSetupCollectionInterface) => {
                    dispatch({
                        type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
                        showRender: true,
                    });
                    dispatch({
                        type: MnoOperatorSetupAction.MNO_OPERATOR_SET_GET_LIST,
                        mvnoOperatorSetupList: data,
                    } as MnoOperatorSetupReducersType);
                    showMainLoader(false);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, filters, orders, reload, showMainLoader]);

    const addNewTemplate = (): void => setNewTemplate(true);

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };
    return (
        <GridContainer>
            <GridItem xs={12} md={12} lg={12}>
                <PaperedTable>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={12} classes={tableControlStickyHeader} align="left">
                                <GridContainer>
                                    <GridItem xs={12} style={{ textAlign: 'left' }}>
                                        {showClearFilterButton && (
                                            <CustomTextButtom callback={clearFilter} callbackParam={{}} label="general:common.clear" size="sm" />
                                        )}
                                        <CustomTextButtom
                                            callback={(): void => {
                                                showMainLoader(true);
                                                setReload(true);
                                            }}
                                            callbackParam={{}}
                                            label="general:common.search"
                                            size="sm"
                                        />
                                        <FilterList />
                                    </GridItem>
                                </GridContainer>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableControlStickyHeader} padding="none" align="left">
                                <I18n trans="general:common.commands" />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="center">
                                <TableHeaderContentCell
                                    title="prov:features.identity_number"
                                    textAlign="center"
                                    fieldName="id"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="number"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="center">
                                <TableHeaderContentCell
                                    title="pools:sim.service_profile"
                                    textAlign="center"
                                    fieldName="displayName"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="center">
                                <TableHeaderContentCell
                                    textAlign="center"
                                    title="prov:features.is_prepaid"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="prepaid"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerBooleanCheckBoxSelectList"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {mvnoOperatorSetupList &&
                            mvnoOperatorSetupList['hydra:member'].map((listItem) => {
                                return (
                                    <ServiceProfileItem
                                        key={'mos' + listItem.id}
                                        item={listItem}
                                        activeRow={activeRow}
                                        setActiveRow={setActiveRow}
                                        viewCallback={viewCallback}
                                        deleteCallback={deleteCallback}
                                    />
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={10}>
                                {mvnoOperatorSetupList && mvnoOperatorSetupList['hydra:view'] && (
                                    <Pager callback={paginationCallbackFunction} views={mvnoOperatorSetupList} />
                                )}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </PaperedTable>
                {UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_TEMPLATE_WRITE) && (
                    <React.Fragment>
                        <FabAddButton callback={addNewTemplate} />
                        <ServiceProfileAddNew
                            open={newTemplate}
                            closeCallback={(): void => setNewTemplate(false)}
                            reloadCallback={(): void => setReload(true)}
                        />
                    </React.Fragment>
                )}
            </GridItem>
        </GridContainer>
    );
};
